import { MainContext } from "@Totum/Main/Context/provider";
import useGroupsListOperations from "@Totum/Groups/Application/hooks/useGroupsListOperations";
import { IGroupsMinimal } from "@Totum/Groups/Domain/types";
import useCurrentGroupId from "@Totum/Groups/State/hooks/useCurrentGroupId";
import { findLowestContext, getSystemId, hasCapability } from "@Totum/Main/Utils";
import AwesomeDebouncePromise from "awesome-debounce-promise";
import { useCallback, useContext, useEffect, useState } from "react";
import { ActiveTabKey, GroupType } from "@Totum/Groups/Constants";
import { GroupContext, SystemContext } from "@Totum/Main/Constants";

/**
 * Presents the `GroupView` component with the UI model and handlers.
 */
const useGroupViewPresenter = () => {
    // ** Constants & Variables ** //
    const [currentGroupId, setCurrentGroupId] = useCurrentGroupId();
    const [activeTabKey, setActiveTabKey] = useState<string>("groups");
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string | undefined>(searchTerm);
    const [usersCurrentPage, setUsersCurrentPage] = useState<number>(0);
    const [groupsCurrentPage, setGroupsCurrentPage] = useState<number>(1);

    const [breadCrumb, setBreadCrumb] = useState<IGroupsMinimal[]>([]);
    const currentGroup = breadCrumb.length > 0 ? breadCrumb[breadCrumb.length - 1] : null;

    const tabList = [
        {
            key: "groups",
            tab: "Groups",
        },
        {
            key: "users",
            tab: "Users",
        },
    ];

    const canCreateRootGroups = hasCapability("UserGroup/Create", GroupContext, getSystemId());
    const canAddToGroups =
        hasCapability("UserGroup/AddMembers", GroupContext, currentGroupId) &&
        currentGroup?.groupType !== GroupType.License;
    const allowRemove = hasCapability("UserGroup/RemoveMembers", GroupContext, currentGroupId);
    const canRemoveBase = findLowestContext("UserGroup/RemoveMembers") === SystemContext;

    // For the groups inspector
    const { setCurrentGroup, setCurrentUser } = useContext(MainContext);

    // ** Hooks ** //
    const { userGroups, isFetchingUserGroups, userGroup, isFetchingUserGroup, groupCredits, deleteGroup } =
        useGroupsListOperations();

    // ** Handlers ** //
    const handleGroupSelect = (group: IGroupsMinimal): void => {
        setCurrentGroupId(group.groupId);
        setCurrentGroup({ instance: group.groupId });
        setBreadCrumb((prev) => [...prev, group]);
        resetListState();
    };

    const handleGroupBack = () => {
        const updatedBreadCrumb = breadCrumb.slice(0, -1);
        const previousUserGroupId = updatedBreadCrumb[updatedBreadCrumb.length - 1]?.groupId;
        setBreadCrumb(updatedBreadCrumb);
        setCurrentGroupId(previousUserGroupId);
        setCurrentGroup(previousUserGroupId ? { instance: previousUserGroupId } : null);
        resetListState();
    };

    const handleGroupUsersSearch = (searchTerm: string) => {
        setSearchTerm(searchTerm);
    };

    const handleDeleteGroup = () => {
        deleteGroup(currentGroupId);
        handleGroupBack();
    };

    const handleTabChange = (key: string) => {
        setActiveTabKey(key);
        setCurrentGroup(key === ActiveTabKey.Users ? { instance: getSystemId() } : null);
        resetListState();
    };

    const handleSearchInputChange = (term: string) => {
        setSearchTerm(term);
    };

    const handleUsersTablePageChange = (page: number) => {
        setUsersCurrentPage(page - 1);
    };

    const handleGroupsTablePageChange = (page: number) => {
        setGroupsCurrentPage(page);
    };

    const applySearchTerm = async (term?: string) => {
        setDebouncedSearchTerm(term);
    };

    const resetListState = () => {
        setSearchTerm("");
        setCurrentUser(null);
        setUsersCurrentPage(0);
        setGroupsCurrentPage(1);
    };

    const debouncePromise = useCallback(AwesomeDebouncePromise(applySearchTerm, 800), []);

    useEffect(() => {
        debouncePromise(searchTerm);
        setUsersCurrentPage(0);
    }, [searchTerm]);

    // ** Exports ** //

    return {
        handlers: {
            handleGroupSelect,
            handleGroupBack,
            handleGroupUsersSearch,
            handleDeleteGroup,
            handleTabChange,
            handleSearchInputChange,
            handleUsersTablePageChange,
            handleGroupsTablePageChange,
        },
        uiModel: {
            //state
            currentGroup,
            currentGroupId,
            breadCrumb,
            isRootGroupView: !breadCrumb.length,
            searchTerm,
            activeTabKey,
            usersCurrentPage,
            groupsCurrentPage,
            debouncedSearchTerm,
            tabList,

            //capabilities
            canCreateRootGroups,
            canAddToGroups,
            allowRemove,
            canRemoveBase,

            //data
            userGroups,
            isFetchingUserGroups,
            userGroup,
            isFetchingUserGroup,
            groupCredits,
        },
    };
};

export default useGroupViewPresenter;
